import React from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { FilterBarRow, InfoTooltip, Tag, TSTFilterBar } from "@impero-com/ui";
import { Column, Table } from "@tanstack/react-table";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";

import { ArrayOfAssessmentDto as GetRiskInsightsDto } from "api/generated/model";
import { FilterBarDateTimeRange } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRange";

import * as T from "../types";

import {
    getFocusFilterValue,
    parseFocusPairs,
    removeFocusPair,
} from "./mapInsightsHeatmap";
import {
    MAP_INSIGHTS_COLUMN_IDS,
    RiskInsightsTableRow,
} from "./mapInsightsModel";
import * as theme from "./theme.css";

interface MapInsightsFilterBarProps {
    table: Table<RiskInsightsTableRow>;
    data: GetRiskInsightsDto;
    start: moment.Moment;
    end: moment.Moment;
    getFilterComponent: (
        column: Column<RiskInsightsTableRow>
    ) => React.ReactNode;
    impactColName: string;
    likelihoodColName: string;
    impactOptions: { label: string }[];
    likelihoodOptions: { label: string }[];
}

export function MapInsightsFilterBar({
    table,
    data,
    start,
    end,
    getFilterComponent,
    impactColName,
    likelihoodColName,
    impactOptions,
    likelihoodOptions,
}: MapInsightsFilterBarProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const focusFilterValue = getFocusFilterValue(table);
    const focusPairs = parseFocusPairs(focusFilterValue);

    return (
        <TSTFilterBar<RiskInsightsTableRow>
            table={table}
            addLabel={t("FilterBar_AddFilter")}
            globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
            getFilterCountLabel={(count) =>
                t("FilterBar_FiltersApplied", { count })
            }
            rowCountLabel={t("FilterBar_RowCount", {
                count: table.getPrePaginationRowModel().rows.length,
            })}
            hideFiltersLabel={t("FilterBar_HideFilters")}
            clearAllLabel={t("FilterBar_ClearAll")}
            getFilterComponent={getFilterComponent}
            aboveFilters={
                <FilterBarRow
                    columnOptions={[]}
                    selectedColumnOption={t("RiskInsights_Filter_DateRange")}
                    // no-op, it's impossible to change this option.
                    onColumnOptionChange={() => {}}
                >
                    <FilterBarDateTimeRange
                        facetedValues={
                            new Map(
                                data.linkedResources.riskMapValidity
                                    .flatMap((validInterval) => [
                                        // When undefined, set the start date to risk history rollout
                                        validInterval.start ??
                                            T.RISK_MAP_HISTORY_FEATURE_AVAILABILITY,
                                        // When undefined (still active), set the end date to today
                                        validInterval.end ??
                                            moment().toISOString(true),
                                    ])
                                    .map((date) => [date, 1] as const)
                            )
                        }
                        filterValue={[start.toISOString(), end.toISOString()]}
                        setFilterValue={(value) => {
                            if (value !== null) {
                                const [start, end] = value;
                                if (start !== null && end !== null) {
                                    const queryParameters = new URLSearchParams(
                                        window.location.search
                                    );
                                    queryParameters.set(
                                        "from",
                                        moment(start)
                                            .endOf("day")
                                            .toISOString(true)
                                    );
                                    queryParameters.set(
                                        "to",
                                        moment(end)
                                            .endOf("day")
                                            .toISOString(true)
                                    );
                                    navigate({
                                        search: queryParameters.toString(),
                                        hash: window.location.hash,
                                    });
                                }
                            }
                        }}
                    />
                    <InfoTooltip
                        className={theme.dateRangeTooltip}
                        text={t("RiskInsights_Filter_DateRange_Tooltip")}
                    />
                </FilterBarRow>
            }
            belowFilters={
                focusPairs.length > 0 && (
                    <FilterBarRow
                        columnOptions={[]}
                        selectedColumnOption={`${impactColName} / ${likelihoodColName}`}
                        // no-op, it's impossible to change this option.
                        onColumnOptionChange={() => {}}
                        onFilterRemoveClick={() =>
                            table
                                .getColumn(MAP_INSIGHTS_COLUMN_IDS.FOCUS)
                                ?.setFilterValue(undefined)
                        }
                    >
                        <div className={theme.datePickerInsights}>
                            {focusPairs
                                // Covers the case where the axis have changed (or the URL have been crafted) and a cell is outside the range.
                                .filter(
                                    ({ x, y }) =>
                                        impactOptions[y] !== undefined &&
                                        likelihoodOptions[x] !== undefined
                                )
                                .map(({ x, y }) => (
                                    <Tag
                                        key={`${x}:${y}`}
                                        closable
                                        onClose={() =>
                                            table
                                                .getColumn(
                                                    MAP_INSIGHTS_COLUMN_IDS.FOCUS
                                                )
                                                ?.setFilterValue(
                                                    removeFocusPair(
                                                        focusFilterValue,
                                                        x,
                                                        y
                                                    )
                                                )
                                        }
                                    >
                                        {`${impactOptions[y].label} / ${likelihoodOptions[x].label}`}
                                    </Tag>
                                ))}
                        </div>
                    </FilterBarRow>
                )
            }
        />
    );
}
